import React, { memo, useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { exerciseDetails, exerciseAddons, saveExercise, newExercise, removeExercise, copyExercise } from "../../services/exercises";
import { categoriesList } from "../../services/categories";
import Switch from "../../components/shared/switch";

import ExerciseSceneEditor from "./editor-canvas";
import { defaultField, field_50, field_25 } from "./fields";

import Select from "react-select";
import ContentTranslation from "../../components/ContentTranslation";
import AgeSlider from "../../components/AgeSlider";
import ImagePicker from "../../components/shared/ImagePicker";
import ConfirmRemoveDialog from '../../components/ConfirmRemoveDialog';
import { selectStyles } from "../../components/shared/styles";
import CommentsButton from '../../components/CommentsButton';
import CategoryDialogButton from '../../components/CategoryDialogButton';
import ReturnButton from '../../components/ReturnButton';

const fieldTemplates = ['default', 'field_50', 'field_25']
import { filterEmptyTranslations } from '../../services/filters';

const ExerciseForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id, clone_id } = useParams();

  const { t, i18n } = useTranslation();

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const [requiredBalls, setRequiredBalls] = useState([])
  const [fieldShapes, setFieldShapes] = useState([])
  const [actionTypes, setActionTypes] = useState([])
  const [categories, setCategories] = useState([])
  const [exercise, setExercise] = useState({ required_balls: 'no_balls', field_shape: 'square', 
    exercise_translations_attributes: [{ title: '', content: '', locale: i18n.language, published: true }], 
    field_template: 'default',
    diagram_data: defaultField
  })

  const loadData = async () => {
    if (id) {
      const { exercise } = await exerciseDetails(id);
      setExercise(exercise);
    } else if (clone_id) {
      try {
        const { exercise } = await copyExercise(clone_id);
        setExercise(exercise);
      } catch (e) {
        navigate(-1)
      }
    } else {
      const data = await newExercise();

      const diagram_data = typeof data.diagram_data === 'string' ? JSON.parse(data.diagram_data) : data.diagram_data;
      setExercise({ ...exercise, diagram_data });
    }
  };

  const loadCategories = async () => {
    const { categories } = await categoriesList();
    setCategories(categories);
  }

  useEffect(() => {loadData()}, [location.pathname])

  useEffect(() => {
    const loadAddons = async () => {
      const { required_balls, field_shapes, action_types } = await exerciseAddons();
      setRequiredBalls(required_balls);
      setFieldShapes(field_shapes);
      setActionTypes(action_types);
    }

    loadAddons();
    loadCategories();
  }, [])

  const updateDiagram = (diagram_data) => {
    setExercise(prevState => {
      return {...prevState, diagram_data}
    })
  }

  const save = async () => {
    const data = { ...exercise, diagram_data: JSON.stringify(exercise.diagram_data)}
    data.exercise_translations_attributes = filterEmptyTranslations(exercise.exercise_translations_attributes)

    const response = await saveExercise(data);
    navigate("/club_admin/exercises/")
  }

  const selectCategory = (categoryId) => {
    let club_category_ids = [...(exercise?.club_category_ids || [])]
    const index = club_category_ids.findIndex(item => item === categoryId)
    club_category_ids = index !== -1 ? club_category_ids.filter(item => item !== categoryId) : [...club_category_ids, categoryId]

    setExercise({...exercise, club_category_ids})
  }

  const removeImage = () => {
    setExercise({...exercise, image: null, delete_image: true })
  }

  const remove = async () => {
    if (!exercise?.id || !!exercise?.locked) {
      return;
    }
    setShowConfirmDialog(true);
  };

  const confirmRemove = async () => {
    await removeExercise(exercise.id)
    navigate("/club_admin/exercises/")
  };

  const clone = () => {
    navigate(`/club_admin/exercises/${id}/copy`)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const changeTemplate = ({ value }) => {
    if (value === exercise.field_template) return;
    const diagram_data = changeFieldTemplate(exercise.field_template, value, exercise.diagram_data)
    setExercise({...exercise, field_template: value, diagram_data })
  }

  return (
    <div className="form-container exercise-form">
      <ReturnButton/>
      <div className="form-container-header">
        { id && t('exercises.edit')}
        { clone_id && t('exercises.copy')}
        { !id && !clone_id && t('exercises.new')}
      </div>
      <div className="form-container-header">
        <div className="form-container-header-title flex1">
          {exercise?.title}
        </div>
        <div className="form-container-header-status">{t('common.published')}<Switch value={exercise?.published} onChange={(value) => setExercise({ ...exercise, published: value })}/></div>
      </div>
      <div className="form-container-area relative">
        <div className='exercise-heading-img-btn'>
          <ImagePicker
            imageUrl={exercise.image || !exercise.delete_image && exercise.image_url}
            onChange={(file) => setExercise({...exercise, image: file, delete_image: !file })}
            onRemove={removeImage}
            crop
            asButton
          />
        </div>
        <ExerciseSceneEditor diagram_data={exercise.diagram_data} image={exercise.image || !exercise.delete_image && exercise.image_url} actionTypes={actionTypes} onChange={(data) => updateDiagram(data)}>
          <div>
            <div className="heading">{t('exercises.required_balls')}</div>
            <Select
              styles={selectStyles}
              closeMenuOnSelect={true}
              options={requiredBalls.map(item => ({ value: item, label: t(`exercises.ball_requirements.${item}`) }))}
              placeholder={t('exercises.placeholder')}
              onChange={(selected) => setExercise({...exercise, required_balls: selected.value })}
              value={{
                value: exercise?.required_balls,
                label: t(`exercises.ball_requirements.${exercise?.required_balls}`)
              }}
            />
            <br />
            <div className="heading">{t('exercises.form.field_shape')}</div>
            <Select
              styles={selectStyles}
              closeMenuOnSelect={true}
              options={fieldShapes.map(item => ({ value: item,  label: t(`exercise_collections.field_shapes.${item}`)}))}
              default={'square'}
              isSearchable={false}
              onChange={(selected) => setExercise({...exercise, field_shape: selected.value })}
              value={{ value: exercise?.field_shape, label: t(`exercise_collections.field_shapes.${exercise?.field_shape}`)}}
            />
            <br />
            <div className="heading">{t('exercises.form.field_template')}</div>
            <Select
              styles={selectStyles}
              closeMenuOnSelect={true}
              options={fieldTemplates.map(item => ({ value: item,  label: t(`exercises.field_templates.${item}`)}))}
              default={'default'}
              isSearchable={false}
              onChange={changeTemplate}
              value={{ value: exercise?.field_shape, label: t(`exercises.field_templates.${exercise?.field_template}`)}}
            />
          </div>
        </ExerciseSceneEditor>
      </div>

      <div className="form-container-content">
        <div className="exercise-form-year_span">
          <div className="heading">{t('common.year_span')}</div>
          <AgeSlider
            minimum_age={exercise?.minimum_age}
            maximum_age={exercise?.maximum_age}
            onChange={({minimum_age, maximum_age}) => setExercise({ ...exercise, minimum_age, maximum_age})}>
          </AgeSlider>
        </div>

        <div className="form-container-content-right-narrow"></div>
      </div>


      <div className="form-container-content">
        <ContentTranslation data={exercise?.exercise_translations_attributes} onChange={values => setExercise({...exercise, exercise_translations_attributes: values})}/>
        {exercise.id && <CommentsButton entityType= {'exercises'}/> }
      </div>

      <div className="form-container-content">
        <div>
          <div className="heading">{t('exercises.form.label')}</div>
          <div className="form-container-content-categories">
            {categories.map(category => (
              <label key={category.id}>
                <input type="checkbox" checked={!!exercise?.club_category_ids?.includes(category.id)} onChange={() => selectCategory(category.id)}/>
                { category.title }
              </label>
            ))}
            <CategoryDialogButton onChange={loadCategories} />
          </div>
        </div>

        <div className="form-container-content-right-narrow"></div>
      </div>

      <div className="form-container-actions">
        <button className='button button-delete' onClick={remove}>{t('buttons.exercise.delete')}</button>
        { id && <button className='button button-clone' onClick={clone}>
          {t('exercises.copy_button')} <i className="icon icon-docs pointer"></i>
        </button> }
        <div className="flex-end">
          <button className="button" onClick={() => navigate("/club_admin/exercises/")}>{t('buttons.common.cancel')} </button>
          <button className="button button-primary" onClick={save}>{t('buttons.common.save-changes')}</button>
        </div>
      </div>

      {showConfirmDialog && (
        <ConfirmRemoveDialog
          title={t(`exercises.remove_exercise`, { title: exercise?.title })}
          confirm={confirmRemove}
          close={() => setShowConfirmDialog(false)}
        />
      )}
    </div>

  )
};

export default memo(ExerciseForm);

const changeFieldTemplate = (from, to, diagram_data) => {
  const templates = { default: defaultField, field_50, field_25 }

  let objects = diagram_data.objects;
  const removable = templates[from].objects || [];

  removable.forEach(o => {
    objects = objects.filter( i => !i.template && !compareFieldObjects(o, i))
  })

  objects = [...objects, ...(templates[to].objects || [])];

  return { ...diagram_data, objects, version: Math.random() }
}

const compareFieldObjects = (a, b) => {
  const arraysEqual = (a, b) => {
    if (a.length !== b.length) return false;
    return a.every((val, index) => val === b[index]);
  }
  
  const type = a.type;
  if (a.type && a.type !== b.type) return false;

  if (type == "cone") {
    return a.variation == b.variation && arraysEqual(a.origin, b.origin);
  }

  if (type == "divider") {
   return arraysEqual(a.origin, b.origin) && a.points.every((val, index) => arraysEqual(val, b.points[index]) === true);
  }

  return true;
}